import { makeAutoObservable, runInAction } from "mobx";
import { validate, type ValidationError } from "class-validator";
import {
  ApiError,
  OpenAPI,
  superadminListPushCampaigns,
  superadminCreatePushCampaign,
  superadminGetPushCampaign,
  superadminUpdatePushCampaign,
  superadminSchedulePushCampaign,
  superadminSendPushCampaignNow,
  superadminCancelPushCampaign,
  superadminDuplicatePushCampaign,
  superadminTestPushCampaign,
  superadminPushCampaignAudiencePreview,
  superadminListPushCampaignRecipients,
  superadminGetClient,
  superadminListPushableClients,
  superadminListServiceCenters,
  type PushCampaignWriteDto,
} from "@pro-sto/api-client";
import type { PaginationMetaDto, SuperadminPushableClientItemDto } from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";
import { PushCampaignFormDto, PushCampaignTestDto } from "./push-campaigns/PushCampaignFormDto";
import { isMoscowQuietHours, QUIET_HOURS_ERROR } from "./push-campaigns/quietHours";
import {
  FUNNEL_STAGES,
  type FunnelStage,
  type PushCampaignAudienceMode,
  type PushCampaignLinkKind,
  isPushCampaignEditable,
} from "./push-campaigns/pushCampaignLabels";

export interface PushCampaignListItem {
  uuid: string;
  name: string;
  status: string;
  scheduledAt?: string | null;
  sentAt?: string | null;
  createdAt?: string | null;
  recipientsTotal?: number;
  deliveredCount?: number;
  openedCount?: number;
  bookedCount?: number;
  paidCount?: number;
  sentCount?: number;
}

export interface PushCampaignDetail extends PushCampaignListItem {
  title?: string;
  subtitle?: string | null;
  body?: string;
  linkKind?: PushCampaignLinkKind;
  linkServiceCenterUuid?: string | null;
  linkServiceCenterName?: string | null;
  targetBusinessType?: string | null;
  audienceMode?: PushCampaignAudienceMode;
  audienceQuery?: string | null;
  clientUuids?: string[];
  attributionWindowHours?: number;
  authorName?: string | null;
  skippedCount?: number;
  failedDeliveryCount?: number;
  revenueRub?: number;
  bookedWithoutOpenCount?: number;
  funnel?: Partial<Record<FunnelStage, number>>;
}

export interface PushCampaignRecipientRow {
  uuid?: string;
  clientUuid: string;
  clientName?: string;
  clientPhone?: string;
  status: string;
  deviceErrorCodes?: string[];
  opened?: boolean;
  funnelStage?: string | null;
  bookingUuid?: string | null;
  bookingStatus?: string | null;
  bookingTotalCost?: number | null;
  isTest?: boolean;
  skipReason?: string | null;
}

export interface PushCampaignFormState {
  name: string;
  title: string;
  subtitle: string;
  body: string;
  linkKind: PushCampaignLinkKind;
  linkServiceCenterUuid: string;
  linkServiceCenterName: string;
  targetBusinessType: "car_wash" | "tire_service" | null;
  audienceMode: PushCampaignAudienceMode;
  audienceQuery: string;
  selectedClientUuids: string[];
  attributionWindowHours: number;
}

export interface AudiencePreview {
  clientsCount: number;
  devicesCount: number;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function pickString(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string") return v;
  }
  return undefined;
}

function pickNumber(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
  }
  return undefined;
}

function pickStringArray(
  obj: Record<string, unknown>,
  ...keys: string[]
): string[] {
  for (const key of keys) {
    const v = obj[key];
    if (Array.isArray(v)) {
      return v.filter((x): x is string => typeof x === "string");
    }
    if (typeof v === "string" && v.trim().startsWith("[")) {
      try {
        const parsed: unknown = JSON.parse(v);
        if (Array.isArray(parsed)) {
          return parsed.filter((x): x is string => typeof x === "string");
        }
      } catch {
        // Поле пришло не JSON-массивом.
      }
    }
  }
  return [];
}

function pickBool(obj: Record<string, unknown>, ...keys: string[]): boolean | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "boolean") return v;
  }
  return undefined;
}

function parseListMeta(
  raw: unknown,
  fallbackLimit: number,
): { items: unknown[]; meta: PaginationMetaDto } {
  const r = asRecord(raw) ?? {};
  const items =
    (r.items as unknown[] | undefined) ??
    (r.data as unknown[] | undefined) ??
    [];
  const nestedMeta = asRecord(r.meta);
  const total = pickNumber(nestedMeta ?? r, "total") ?? 0;
  const page = pickNumber(nestedMeta ?? r, "page") ?? 1;
  const limit = pickNumber(nestedMeta ?? r, "limit") ?? fallbackLimit;
  const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;
  return {
    items,
    meta: {
      total,
      page,
      limit,
      totalPages,
      hasNextPage: nestedMeta?.hasNextPage === true || page < totalPages,
      hasPrevPage: nestedMeta?.hasPrevPage === true || page > 1,
    },
  };
}

function parseCampaignListItem(raw: unknown): PushCampaignListItem | null {
  const o = asRecord(raw);
  if (!o) return null;
  const uuid = pickString(o, "uuid", "id");
  if (!uuid) return null;
  return {
    uuid,
    name: pickString(o, "name") ?? "—",
    status: pickString(o, "status") ?? "draft",
    scheduledAt: pickString(o, "scheduled_at", "scheduledAt") ?? null,
    sentAt: pickString(o, "sent_at", "sentAt") ?? null,
    createdAt: pickString(o, "created_at", "createdAt") ?? null,
    recipientsTotal: pickNumber(o, "recipients_total", "recipientsTotal"),
    deliveredCount: pickNumber(o, "delivered_count", "deliveredCount"),
    openedCount: pickNumber(o, "opened_count", "openedCount"),
    bookedCount: pickNumber(
      o,
      "booking_created_count",
      "booked_count",
      "bookedCount",
    ),
    paidCount: pickNumber(o, "booking_paid_count", "paid_count", "paidCount"),
    sentCount: pickNumber(o, "sent_count", "sentCount"),
  };
}

function parseCampaignDetail(raw: unknown): PushCampaignDetail | null {
  const base = parseCampaignListItem(raw);
  const o = asRecord(raw);
  if (!base || !o) return null;
  const funnel: Partial<Record<FunnelStage, number>> = {};
  const funnelRaw = asRecord(o.funnel) ?? asRecord(o.funnel_counts) ?? o;
  for (const stage of FUNNEL_STAGES) {
    const v = pickNumber(funnelRaw, stage, `${stage}_count`);
    if (v !== undefined) funnel[stage] = v;
  }
  return {
    ...base,
    title: pickString(o, "title"),
    subtitle: pickString(o, "subtitle") ?? null,
    body: pickString(o, "body"),
    linkKind: (pickString(o, "link_kind", "linkKind") as PushCampaignLinkKind) ?? "none",
    linkServiceCenterUuid:
      pickString(o, "link_service_center_uuid", "linkServiceCenterUuid") ?? null,
    linkServiceCenterName:
      pickString(o, "link_service_center_name", "linkServiceCenterName") ?? null,
    targetBusinessType:
      pickString(o, "target_business_type", "targetBusinessType") ?? null,
    audienceMode:
      (pickString(o, "audience_mode", "audienceMode") as PushCampaignAudienceMode) ??
      "all_matching",
    audienceQuery: pickString(o, "audience_query", "audienceQuery") ?? null,
    clientUuids: pickStringArray(
      o,
      "audience_client_uuids",
      "audienceClientUuids",
      "client_uuids",
      "clientUuids",
    ),
    attributionWindowHours:
      pickNumber(o, "attribution_window_hours", "attributionWindowHours") ?? 72,
    authorName:
      pickString(o, "created_by_name", "author_name", "authorName") ??
      pickString(asRecord(o.author) ?? {}, "name"),
    skippedCount: pickNumber(o, "skipped_count", "skippedCount"),
    failedDeliveryCount: pickNumber(
      o,
      "failed_count",
      "failed_delivery_count",
      "failedDeliveryCount",
    ),
    revenueRub: pickNumber(o, "paid_revenue", "revenue_rub", "revenueRub", "revenue"),
    bookedWithoutOpenCount: pickNumber(
      o,
      "booked_without_open_count",
      "bookedWithoutOpenCount",
    ),
    funnel,
  };
}

function parseRecipient(raw: unknown): PushCampaignRecipientRow | null {
  const o = asRecord(raw);
  if (!o) return null;
  const client = asRecord(o.client);
  const booking = asRecord(o.booking);
  const clientUuid =
    pickString(client ?? {}, "uuid") ??
    pickString(o, "client_uuid", "clientUuid") ??
    "";
  if (!clientUuid) return null;
  const deviceErrors = [
    ...readDeviceErrorCodes(o.devices),
    ...(Array.isArray(o.device_error_codes)
      ? o.device_error_codes.filter((item): item is string => typeof item === "string")
      : []),
    ...(Array.isArray(o.deviceErrorCodes)
      ? o.deviceErrorCodes.filter((item): item is string => typeof item === "string")
      : []),
  ];
  return {
    uuid: pickString(o, "uuid", "recipient_uuid"),
    clientUuid,
    clientName:
      pickString(client ?? {}, "name") ??
      pickString(o, "client_name", "clientName") ??
      undefined,
    clientPhone:
      pickString(client ?? {}, "phone") ??
      pickString(o, "client_phone", "clientPhone") ??
      undefined,
    status: pickString(o, "status") ?? "pending",
    deviceErrorCodes: deviceErrors.length > 0 ? deviceErrors : undefined,
    opened: readOpened(o),
    funnelStage: pickString(o, "funnel_stage", "funnelStage") ?? null,
    bookingUuid:
      pickString(booking ?? {}, "uuid") ??
      pickString(o, "booking_uuid", "bookingUuid") ??
      null,
    bookingStatus: pickString(booking ?? {}, "status") ?? null,
    bookingTotalCost:
      pickNumber(booking ?? {}, "total_cost", "totalCost") ?? null,
    isTest: pickBool(o, "is_test", "isTest"),
    skipReason: pickString(o, "skip_reason", "skipReason") ?? null,
  };
}

function readDeviceErrorCodes(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const codes: string[] = [];
  for (const item of raw) {
    const code = pickString(asRecord(item) ?? {}, "error_code", "errorCode");
    if (code) codes.push(code);
  }
  return codes;
}

function readOpened(raw: Record<string, unknown>): boolean | undefined {
  const explicit = pickBool(raw, "opened");
  if (explicit !== undefined) return explicit;
  if (!("opened_at" in raw) && !("openedAt" in raw)) return undefined;
  const value = raw.opened_at ?? raw.openedAt;
  return value != null && value !== "";
}

function extractApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = asRecord(err.body);
    const message = body?.message;
    if (typeof message === "string" && message.length > 0) return message;
    if (Array.isArray(message) && typeof message[0] === "string") return message[0];
  }
  return fallback;
}

const VALIDATION_FIELD_UI_KEYS: Record<string, string> = {
  link_service_center_uuid: "linkServiceCenterUuid",
  client_uuids: "client_uuids",
  attribution_window_hours: "attributionWindowHours",
  link_kind: "linkKind",
  audience_mode: "audienceMode",
};

function mapValidationErrors(errors: ValidationError[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const error of errors) {
    if (error.constraints) {
      const key =
        VALIDATION_FIELD_UI_KEYS[error.property] ?? error.property;
      result[key] = Object.values(error.constraints)[0] ?? "";
    }
  }
  return result;
}

function buildRecipientsExportUrl(
  uuid: string,
  filters: {
    q: string;
    status: string;
    opened: string;
    funnelStage: string;
  },
): string {
  const base = OpenAPI.BASE || "";
  const params = new URLSearchParams({
    q: filters.q,
    status: filters.status,
    opened: filters.opened,
    funnel_stage: filters.funnelStage,
  });
  return `${base}/api/superadmin/push-campaigns/${uuid}/recipients/export?${params.toString()}`;
}

function emptyAudienceClient(uuid: string): SuperadminPushableClientItemDto {
  return {
    uuid,
    name: null,
    phone: null,
    email: null,
    created_at: "",
    activeDevicesCount: 0,
  };
}

const emptyForm = (): PushCampaignFormState => ({
  name: "",
  title: "",
  subtitle: "",
  body: "",
  linkKind: "none",
  linkServiceCenterUuid: "",
  linkServiceCenterName: "",
  targetBusinessType: null,
  audienceMode: "all_matching",
  audienceQuery: "",
  selectedClientUuids: [],
  attributionWindowHours: 72,
});

class PushCampaignsStore {
  campaigns: PushCampaignListItem[] = [];
  listMeta: PaginationMetaDto | null = null;
  listSearch = "";
  listStatus = "";
  listSort = "-created_at";
  listPage = 1;
  listLimit = 20;
  isListLoading = false;
  listError: string | null = null;

  detail: PushCampaignDetail | null = null;
  isDetailLoading = false;
  detailPollingTimer: ReturnType<typeof setInterval> | null = null;

  recipients: PushCampaignRecipientRow[] = [];
  recipientsMeta: PaginationMetaDto | null = null;
  recipientsSearch = "";
  recipientsStatus = "";
  recipientsOpened = "";
  recipientsFunnelStage = "";
  recipientsPage = 1;
  recipientsLimit = 20;
  isRecipientsLoading = false;

  form: PushCampaignFormState = emptyForm();
  editingUuid: string | null = null;
  isFormLoading = false;
  isSaving = false;
  validationErrors: Record<string, string> = {};

  audiencePreview: AudiencePreview | null = null;
  isAudiencePreviewLoading = false;
  audiencePreviewTimer: ReturnType<typeof setTimeout> | null = null;

  pushableClients: SuperadminPushableClientItemDto[] = [];
  audienceClientCards: SuperadminPushableClientItemDto[] = [];
  pushableSearch = "";
  isPushableLoading = false;
  testSelectedUuids: string[] = [];

  serviceCenterOptions: Array<{ uuid: string; name: string }> = [];
  serviceCenterSearch = "";
  isServiceCentersLoading = false;

  scheduleDate = "";
  scheduleTime = "";

  isExportingCsv = false;

  editorCampaignStatus: string | null = null;
  editorLoadedUuid: string | null = null;

  listRequestId = 0;
  detailRequestId = 0;
  recipientsRequestId = 0;
  editorRequestId = 0;

  constructor() {
    makeAutoObservable(this, {
      detailPollingTimer: false,
      audiencePreviewTimer: false,
      listRequestId: false,
      detailRequestId: false,
      recipientsRequestId: false,
      editorRequestId: false,
    });
  }

  resetForm = () => {
    this.form = emptyForm();
    this.editingUuid = null;
    this.editorCampaignStatus = null;
    this.editorLoadedUuid = null;
    this.validationErrors = {};
    this.scheduleDate = "";
    this.scheduleTime = "";
    this.testSelectedUuids = [];
    this.audiencePreview = null;
    this.audienceClientCards = [];
  };

  get audiencePickerClients(): SuperadminPushableClientItemDto[] {
    const selectedIds = this.form.selectedClientUuids;
    const selectedSet = new Set(selectedIds);
    const byId = new Map<string, SuperadminPushableClientItemDto>();
    for (const client of this.audienceClientCards) byId.set(client.uuid, client);
    for (const client of this.pushableClients) {
      if (!byId.has(client.uuid)) byId.set(client.uuid, client);
    }
    const selected = selectedIds.map(
      (id) => byId.get(id) ?? emptyAudienceClient(id),
    );
    const rest = this.pushableClients.filter((client) => !selectedSet.has(client.uuid));
    return [...selected, ...rest];
  }

  loadCampaigns = async () => {
    const requestId = ++this.listRequestId;
    this.isListLoading = true;
    this.listError = null;
    try {
      const raw = await superadminListPushCampaigns({
        page: this.listPage,
        limit: this.listLimit,
        q: this.listSearch,
        status: this.listStatus,
        sort: this.listSort,
      });
      const { items, meta } = parseListMeta(raw, this.listLimit);
      runInAction(() => {
        if (requestId !== this.listRequestId) return;
        this.campaigns = items
          .map(parseCampaignListItem)
          .filter((x): x is PushCampaignListItem => x !== null);
        this.listMeta = meta;
        this.listError = null;
        this.isListLoading = false;
      });
    } catch (err) {
      const message = extractApiError(
        err,
        "Не удалось загрузить рассылки. Проверьте доступ к API push-campaigns на стенде.",
      );
      runInAction(() => {
        if (requestId !== this.listRequestId) return;
        this.campaigns = [];
        this.listMeta = null;
        this.listError = message;
        this.isListLoading = false;
      });
      if (requestId === this.listRequestId) {
        toastStore.showError(message);
      }
    }
  };

  setListSearch = (value: string) => {
    this.listSearch = value;
    this.listPage = 1;
    void this.loadCampaigns();
  };

  setListStatus = (value: string) => {
    this.listStatus = value === "all" ? "" : value;
    this.listPage = 1;
    void this.loadCampaigns();
  };

  setListPage = (page: number) => {
    this.listPage = page;
    void this.loadCampaigns();
  };

  loadCampaignDetail = async (uuid: string, silent = false): Promise<boolean> => {
    const requestId = ++this.detailRequestId;
    if (!silent) this.isDetailLoading = true;
    try {
      const raw = await superadminGetPushCampaign({ uuid });
      const detail = parseCampaignDetail(raw);
      runInAction(() => {
        if (requestId !== this.detailRequestId) return;
        this.detail = detail;
        if (!silent) this.isDetailLoading = false;
      });
      if (requestId === this.detailRequestId) {
        this.syncDetailPolling();
      }
      return requestId === this.detailRequestId;
    } catch {
      runInAction(() => {
        if (requestId !== this.detailRequestId) return;
        if (!silent) this.isDetailLoading = false;
      });
      if (!silent && requestId === this.detailRequestId) {
        toastStore.showError("Не удалось загрузить рассылку");
      }
      return false;
    }
  };

  stopDetailPolling = () => {
    if (this.detailPollingTimer) {
      clearInterval(this.detailPollingTimer);
      this.detailPollingTimer = null;
    }
  };

  syncDetailPolling = () => {
    this.stopDetailPolling();
    const uuid = this.detail?.uuid;
    if (!uuid || this.detail?.status !== "sending") return;
    this.detailPollingTimer = setInterval(() => {
      void this.loadCampaignDetail(uuid, true);
      void this.loadRecipients(uuid, true);
    }, 4_000);
  };

  loadRecipients = async (uuid: string, silent = false) => {
    const requestId = ++this.recipientsRequestId;
    if (!silent) this.isRecipientsLoading = true;
    try {
      const raw = await superadminListPushCampaignRecipients({
        uuid,
        page: this.recipientsPage,
        limit: this.recipientsLimit,
        q: this.recipientsSearch,
        status: this.recipientsStatus,
        opened: this.recipientsOpened,
        funnelStage: this.recipientsFunnelStage,
      });
      const { items, meta } = parseListMeta(raw, this.recipientsLimit);
      runInAction(() => {
        if (requestId !== this.recipientsRequestId) return;
        this.recipients = items
          .map(parseRecipient)
          .filter((x): x is PushCampaignRecipientRow => x !== null);
        this.recipientsMeta = meta;
        if (!silent) this.isRecipientsLoading = false;
      });
    } catch {
      runInAction(() => {
        if (requestId !== this.recipientsRequestId) return;
        if (!silent) this.isRecipientsLoading = false;
      });
      if (!silent && requestId === this.recipientsRequestId) {
        toastStore.showError("Не удалось загрузить получателей");
      }
    }
  };

  setRecipientsFilters = (patch: {
    search?: string;
    status?: string;
    opened?: string;
    funnelStage?: string;
  }) => {
    if (patch.search !== undefined) this.recipientsSearch = patch.search;
    if (patch.status !== undefined) {
      this.recipientsStatus = patch.status === "all" ? "" : patch.status;
    }
    if (patch.opened !== undefined) {
      this.recipientsOpened = patch.opened === "all" ? "" : patch.opened;
    }
    if (patch.funnelStage !== undefined) {
      this.recipientsFunnelStage =
        patch.funnelStage === "all" ? "" : patch.funnelStage;
    }
    this.recipientsPage = 1;
    const uuid = this.detail?.uuid;
    if (uuid) void this.loadRecipients(uuid);
  };

  setRecipientsPage = (page: number) => {
    this.recipientsPage = page;
    const uuid = this.detail?.uuid;
    if (uuid) void this.loadRecipients(uuid);
  };

  loadEditor = async (uuid?: string) => {
    const requestId = ++this.editorRequestId;
    this.isFormLoading = true;
    try {
      if (!uuid) {
        runInAction(() => {
          if (requestId !== this.editorRequestId) return;
          this.resetForm();
          this.isFormLoading = false;
        });
        if (requestId === this.editorRequestId) {
          this.scheduleAudiencePreview();
        }
        return;
      }
      const raw = await superadminGetPushCampaign({ uuid });
      const detail = parseCampaignDetail(raw);
      runInAction(() => {
        if (requestId !== this.editorRequestId) return;
        if (!detail || detail.uuid !== uuid) {
          this.isFormLoading = false;
          return;
        }
        this.editingUuid = detail.uuid;
        this.editorLoadedUuid = detail.uuid;
        this.editorCampaignStatus = detail.status;
        this.form = {
          name: detail.name,
          title: detail.title ?? "",
          subtitle: detail.subtitle ?? "",
          body: detail.body ?? "",
          linkKind: detail.linkKind ?? "none",
          linkServiceCenterUuid: detail.linkServiceCenterUuid ?? "",
          linkServiceCenterName: detail.linkServiceCenterName ?? "",
          targetBusinessType:
            detail.targetBusinessType === "car_wash" ||
            detail.targetBusinessType === "tire_service"
              ? detail.targetBusinessType
              : null,
          audienceMode: detail.audienceMode ?? "all_matching",
          audienceQuery: detail.audienceQuery ?? "",
          selectedClientUuids: detail.clientUuids ?? [],
          attributionWindowHours: detail.attributionWindowHours ?? 72,
        };
        this.isFormLoading = false;
      });
      if (requestId === this.editorRequestId) {
        this.scheduleAudiencePreview();
        void this.hydrateAudienceClients(detail?.clientUuids ?? []);
      }
    } catch {
      runInAction(() => {
        if (requestId !== this.editorRequestId) return;
        this.isFormLoading = false;
      });
      if (requestId === this.editorRequestId) {
        toastStore.showError("Не удалось загрузить рассылку");
      }
    }
  };

  private buildWriteBody(): PushCampaignWriteDto {
    const f = this.form;
    // Generated client types nullable strings as objects; cast at the boundary.
    return {
      name: f.name.trim(),
      title: f.title.trim(),
      body: f.body.trim(),
      subtitle: f.subtitle.trim() || null,
      link_kind: f.linkKind,
      link_service_center_uuid:
        f.linkKind === "service_center" && f.linkServiceCenterUuid
          ? f.linkServiceCenterUuid
          : null,
      target_business_type: f.targetBusinessType,
      audience_mode: f.audienceMode,
      audience_query:
        f.audienceMode === "all_matching"
          ? f.audienceQuery.trim() || null
          : null,
      client_uuids:
        f.audienceMode === "client_uuids" ? f.selectedClientUuids : [],
      attribution_window_hours: f.attributionWindowHours,
    } as unknown as PushCampaignWriteDto;
  }

  private async validateForm(): Promise<boolean> {
    const f = this.form;
    const dto = Object.assign(new PushCampaignFormDto(), {
      name: f.name,
      title: f.title,
      body: f.body,
      subtitle: f.subtitle,
      link_kind: f.linkKind,
      link_service_center_uuid: f.linkServiceCenterUuid,
      target_business_type: f.targetBusinessType,
      audience_mode: f.audienceMode,
      client_uuids: f.selectedClientUuids,
      audience_query: f.audienceQuery,
      attribution_window_hours: f.attributionWindowHours,
    });
    const errors = await validate(dto);
    runInAction(() => {
      this.validationErrors = mapValidationErrors(errors);
    });
    return errors.length === 0;
  }

  saveDraft = async (options?: { silent?: boolean }): Promise<string | null> => {
    if (!(await this.validateForm())) return null;
    this.isSaving = true;
    try {
      const body = this.buildWriteBody();
      let uuid = this.editingUuid;
      if (uuid) {
        await superadminUpdatePushCampaign({ uuid, requestBody: body });
      } else {
        const created = await superadminCreatePushCampaign({ requestBody: body });
        const o = asRecord(created);
        uuid =
          pickString(o ?? {}, "uuid", "id") ??
          pickString(asRecord(o?.data) ?? {}, "uuid", "id") ??
          null;
      }
      runInAction(() => {
        this.isSaving = false;
        if (uuid) {
          this.editingUuid = uuid;
          this.editorLoadedUuid = uuid;
        }
      });
      if (!options?.silent) toastStore.showSuccess("Черновик сохранён");
      return uuid;
    } catch (err) {
      runInAction(() => {
        this.isSaving = false;
      });
      toastStore.showError(extractApiError(err, "Не удалось сохранить черновик"));
      return null;
    }
  };

  ensureCampaignUuid = async (options?: {
    silent?: boolean;
  }): Promise<string | null> => this.saveDraft(options);

  sendTest = async (): Promise<boolean> => {
    if (!(await this.validateForm())) return false;
    const testDto = Object.assign(new PushCampaignTestDto(), {
      client_uuids: this.testSelectedUuids,
    });
    const errors = await validate(testDto);
    if (errors.length > 0) {
      runInAction(() => {
        this.validationErrors = {
          ...this.validationErrors,
          test: Object.values(errors[0].constraints ?? {})[0] ?? "",
        };
      });
      return false;
    }
    const uuid = await this.saveDraft({ silent: true });
    if (!uuid) return false;
    try {
      await superadminTestPushCampaign({
        uuid,
        requestBody: { client_uuids: this.testSelectedUuids },
      });
      toastStore.showSuccess("Тестовая отправка запущена");
      return true;
    } catch (err) {
      toastStore.showError(extractApiError(err, "Не удалось отправить тест"));
      return false;
    }
  };

  scheduleCampaign = async (): Promise<boolean> => {
    if (!(await this.validateForm())) return false;
    if (!this.scheduleDate || !this.scheduleTime) {
      runInAction(() => {
        this.validationErrors = {
          ...this.validationErrors,
          schedule: "Укажите дату и время отправки",
        };
      });
      return false;
    }
    const local = new Date(`${this.scheduleDate}T${this.scheduleTime}`);
    if (Number.isNaN(local.getTime()) || local.getTime() <= Date.now()) {
      runInAction(() => {
        this.validationErrors = {
          ...this.validationErrors,
          schedule: "Время отправки должно быть в будущем",
        };
      });
      return false;
    }
    if (isMoscowQuietHours(local)) {
      toastStore.showError(QUIET_HOURS_ERROR);
      return false;
    }
    const uuid = await this.saveDraft({ silent: true });
    if (!uuid) return false;
    try {
      await superadminSchedulePushCampaign({
        uuid,
        requestBody: { scheduled_at: local.toISOString() },
      });
      await this.loadCampaignDetail(uuid);
      toastStore.showSuccess("Рассылка запланирована");
      return true;
    } catch (err) {
      toastStore.showError(extractApiError(err, "Не удалось запланировать рассылку"));
      return false;
    }
  };

  sendNow = async (): Promise<boolean> => {
    if (!(await this.validateForm())) return false;
    if (isMoscowQuietHours(new Date())) {
      toastStore.showError(QUIET_HOURS_ERROR);
      return false;
    }
    const uuid = await this.saveDraft({ silent: true });
    if (!uuid) return false;
    try {
      await superadminSendPushCampaignNow({ uuid });
      await this.loadCampaignDetail(uuid);
      this.adoptEditorStatus(uuid);
      toastStore.showSuccess("Рассылка отправляется");
      return true;
    } catch (err) {
      const unknownStatus =
        !(err instanceof ApiError) ||
        err.status === 0 ||
        err.status === 504 ||
        err.status >= 500;
      if (unknownStatus) {
        toastStore.showError("Статус отправки неизвестен, обновите страницу");
        this.lockEditorAsSending(uuid);
        const loaded = await this.loadCampaignDetail(uuid, true);
        if (loaded) this.adoptEditorStatus(uuid);
        return false;
      }
      toastStore.showError(extractApiError(err, "Не удалось отправить рассылку"));
      return false;
    }
  };

  private lockEditorAsSending(uuid: string) {
    runInAction(() => {
      if (this.editingUuid !== uuid && this.editorLoadedUuid !== uuid) return;
      this.editorCampaignStatus = "sending";
    });
  }

  private adoptEditorStatus(uuid: string) {
    runInAction(() => {
      if (this.detail?.uuid !== uuid) return;
      if (this.editingUuid !== uuid && this.editorLoadedUuid !== uuid) return;
      this.editorCampaignStatus = this.detail.status;
    });
  }

  cancelCampaign = async (uuid: string): Promise<boolean> => {
    try {
      await superadminCancelPushCampaign({ uuid });
      await this.loadCampaignDetail(uuid);
      toastStore.showSuccess("Рассылка отменена");
      return true;
    } catch (err) {
      toastStore.showError(extractApiError(err, "Не удалось отменить рассылку"));
      return false;
    }
  };

  duplicateCampaign = async (uuid: string): Promise<string | null> => {
    try {
      const raw = await superadminDuplicatePushCampaign({ uuid });
      const o = asRecord(raw);
      const newUuid =
        pickString(o ?? {}, "uuid", "id") ??
        pickString(asRecord(o?.data) ?? {}, "uuid", "id") ??
        null;
      if (newUuid) toastStore.showSuccess("Копия создана");
      return newUuid;
    } catch (err) {
      toastStore.showError(extractApiError(err, "Не удалось дублировать"));
      return null;
    }
  };

  scheduleAudiencePreview = () => {
    if (this.audiencePreviewTimer) clearTimeout(this.audiencePreviewTimer);
    this.audiencePreviewTimer = setTimeout(() => {
      void this.loadAudiencePreview();
    }, 350);
  };

  loadAudiencePreview = async () => {
    this.isAudiencePreviewLoading = true;
    try {
      const raw = await superadminPushCampaignAudiencePreview({
        audienceMode: this.form.audienceMode,
        q:
          this.form.audienceMode === "all_matching"
            ? this.form.audienceQuery
            : undefined,
        clientUuids:
          this.form.audienceMode === "client_uuids"
            ? this.form.selectedClientUuids
            : undefined,
      });
      const o = asRecord(raw) ?? asRecord(asRecord(raw)?.data);
      runInAction(() => {
        this.audiencePreview = {
          clientsCount:
            pickNumber(o ?? {}, "clients_count", "clientsCount") ?? 0,
          devicesCount:
            pickNumber(
              o ?? {},
              "active_devices_count",
              "activeDevicesCount",
              "devices_count",
              "devicesCount",
            ) ?? 0,
        };
        this.isAudiencePreviewLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isAudiencePreviewLoading = false;
      });
    }
  };

  loadPushableClients = async () => {
    this.isPushableLoading = true;
    try {
      const raw = await superadminListPushableClients({
        page: 1,
        limit: 50,
        q: this.pushableSearch,
      });
      const { items } = parseListMeta(raw, 50);
      runInAction(() => {
        this.pushableClients = items as SuperadminPushableClientItemDto[];
        for (const client of this.pushableClients) {
          if (this.form.selectedClientUuids.includes(client.uuid)) {
            this.rememberAudienceClient(client);
          }
        }
        this.isPushableLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isPushableLoading = false;
      });
    }
  };

  loadServiceCenters = async () => {
    this.isServiceCentersLoading = true;
    try {
      const raw = await superadminListServiceCenters({
        page: 1,
        limit: 30,
        q: this.serviceCenterSearch,
      });
      const { items } = parseListMeta(raw, 30);
      runInAction(() => {
        this.serviceCenterOptions = items
          .map((item) => {
            const o = asRecord(item);
            const uuid = pickString(o ?? {}, "uuid", "id");
            const name = pickString(o ?? {}, "name") ?? "—";
            return uuid ? { uuid, name } : null;
          })
          .filter((x): x is { uuid: string; name: string } => x !== null);
        this.isServiceCentersLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isServiceCentersLoading = false;
      });
    }
  };

  exportRecipientsCsv = async (uuid: string) => {
    if (this.isExportingCsv) return;
    this.isExportingCsv = true;
    const token =
      typeof OpenAPI.TOKEN === "string"
        ? OpenAPI.TOKEN
        : (localStorage.getItem("accessToken") ?? "");
    const url = buildRecipientsExportUrl(uuid, {
      q: this.recipientsSearch,
      status: this.recipientsStatus,
      opened: this.recipientsOpened,
      funnelStage: this.recipientsFunnelStage,
    });
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match?.[1] ?? "push-campaign-recipients.csv";
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
    } catch {
      toastStore.showError("Не удалось выгрузить CSV");
    } finally {
      runInAction(() => {
        this.isExportingCsv = false;
      });
    }
  };

  toggleSelectedClient = (clientUuid: string) => {
    const set = new Set(this.form.selectedClientUuids);
    if (set.has(clientUuid)) {
      set.delete(clientUuid);
      this.audienceClientCards = this.audienceClientCards.filter(
        (client) => client.uuid !== clientUuid,
      );
    } else if (set.size < 500) {
      set.add(clientUuid);
      const known =
        this.pushableClients.find((client) => client.uuid === clientUuid) ??
        this.audienceClientCards.find((client) => client.uuid === clientUuid);
      if (known) this.rememberAudienceClient(known);
    }
    this.form.selectedClientUuids = [...set];
    this.scheduleAudiencePreview();
  };

  hydrateAudienceClients = async (uuids: string[]) => {
    const requestUuid = this.editingUuid;
    runInAction(() => {
      for (const client of this.pushableClients) {
        if (uuids.includes(client.uuid)) this.rememberAudienceClient(client);
      }
    });
    const known = new Set([
      ...this.audienceClientCards.map((client) => client.uuid),
      ...this.pushableClients.map((client) => client.uuid),
    ]);
    const missing = uuids.filter((uuid) => !known.has(uuid));
    if (missing.length === 0) return;

    const loaded = await Promise.all(
      missing.map(async (uuid) => {
        try {
          const data = await superadminGetClient({ uuid });
          const name = [data.firstName, data.lastName]
            .map((part) => part?.trim())
            .filter((part): part is string => Boolean(part))
            .join(" ");
          return {
            uuid: data.uuid || uuid,
            name: name || null,
            phone: data.phone ?? null,
            email: data.email ?? null,
            created_at: data.registeredAt ?? "",
            activeDevicesCount: 0,
          } satisfies SuperadminPushableClientItemDto;
        } catch {
          return emptyAudienceClient(uuid);
        }
      }),
    );

    runInAction(() => {
      if (this.editingUuid !== requestUuid) return;
      for (const client of loaded) {
        if (this.form.selectedClientUuids.includes(client.uuid)) {
          this.rememberAudienceClient(client);
        }
      }
    });
  };

  private rememberAudienceClient(client: SuperadminPushableClientItemDto) {
    const rest = this.audienceClientCards.filter((item) => item.uuid !== client.uuid);
    this.audienceClientCards = [client, ...rest];
  }

  toggleTestClient = (clientUuid: string) => {
    const set = new Set(this.testSelectedUuids);
    if (set.has(clientUuid)) set.delete(clientUuid);
    else if (set.size < 5) set.add(clientUuid);
    this.testSelectedUuids = [...set];
  };

  disposeDetail = () => {
    this.stopDetailPolling();
    this.detail = null;
    this.recipients = [];
    this.recipientsMeta = null;
    this.detailRequestId += 1;
    this.recipientsRequestId += 1;
  };

  disposeEditor = () => {
    this.stopDetailPolling();
    this.editorRequestId += 1;
    this.resetForm();
  };

  isEditorLocked = (routeUuid: string | undefined): boolean => {
    if (!routeUuid || this.isFormLoading) return false;
    if (this.editorLoadedUuid !== routeUuid) return false;
    return !isPushCampaignEditable(this.editorCampaignStatus);
  };
}

export const pushCampaignsStore = new PushCampaignsStore();
export type { PushCampaignsStore };
